import React from 'react';
import { NavLink } from 'react-router';
import {
  LayoutDashboard, Truck, Wrench,
  ChevronDown, ChevronRight, Car, Calendar, FileCheck, FileText, ShieldCheck, LayoutGrid,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/hooks/use-permissions';

export interface NavItem {
  to: string;
  label: string;
  /** Resource slug — when set, the item is hidden unless the user has
   *  view permission on it. Items without a slug are visible to everyone. */
  slug?: string;
}
export interface NavGroup { label: string; icon: React.ComponentType<{ className?: string }>; items: NavItem[] }

/** Sidebar nav structure — also used by `useDefaultHomePath` to pick the
 *  first item the user can access for the post-login redirect. The order
 *  below is the visual order in the sidebar, top to bottom. */
export const SIDEBAR_GROUPS: NavGroup[] = [
  { label: 'Dashboard', icon: LayoutDashboard, items: [{ to: '/dashboard', label: 'Dashboard', slug: 'dashboard' }] },
  { label: 'Widget', icon: LayoutGrid, items: [{ to: '/widget', label: 'Widgets', slug: 'widget_page' }] },
  {
    label: 'Assets',
    icon: Car,
    items: [
      // Archived / Disposed / Contractor Vehicles are now tabs inside the
      // Assets report rather than separate sidebar entries. The lifecycle
      // tab strip on /fleet/assets navigates between them.
      { to: '/fleet/assets', label: 'Assets', slug: 'assets_page' },
      { to: '/fleet-extras/asset-audit-trail', label: 'Asset Audit Trail', slug: 'asset_audit_trail_page' },
      { to: '/fleet-extras/onboard-assets', label: 'Onboard Asset', slug: 'asset_onboarding_page' },
      { to: '/fleet-extras/offboard-asset', label: 'Offboard Asset', slug: 'asset_offboarding_page' },
      { to: '/fleet-extras/asset-transfers', label: 'Transfer Asset', slug: 'asset_transfer_page' },
      { to: '/vehicles/tracker', label: 'Asset Tracker', slug: 'assets_page' },
    ],
  },
  {
    label: 'Servicing',
    icon: Wrench,
    items: [
      { to: '/servicing/dashboard', label: 'Service Dashboard', slug: 'service_dashboard_page' },
      { to: '/servicing/history', label: 'Log Services Report', slug: 'services_page' },
    ],
  },
  {
    label: 'Compliance',
    icon: ShieldCheck,
    items: [
      { to: '/compliance/license-renew', label: 'Renew a Licence', slug: 'license_renewals_page' },
      { to: '/compliance/wof-renew', label: 'Update WoF', slug: 'wof_renewals_page' },
    ],
  },
  {
    label: 'Job Cards',
    icon: FileText,
    items: [
      { to: '/job-cards/dashboard', label: 'Job Card Dashboard', slug: 'job_card_dashboard_page' },
      { to: '/fleet/job-cards', label: 'Job Cards Report', slug: 'job_cards_page' },
      { to: '/operations/time-logs', label: 'Time Logs', slug: 'time_logs_page' },
    ],
  },
  {
    label: 'Dispatches',
    icon: Calendar,
    items: [
      { to: '/dispatch/schedule', label: 'Dispatch Dashboard', slug: 'dispatch_dashboard_page' },
      { to: '/dispatch/jobs', label: 'Dispatch Jobs Report', slug: 'dispatch_page' },
      { to: '/dispatch-extras/run-sheets', label: 'Run Sheets', slug: 'run_sheets_page' },
      { to: '/my-dispatch', label: 'My Dispatch (Driver)', slug: 'my_dispatch_page' },
    ],
  },
  {
    label: 'Admin',
    icon: FileCheck,
    items: [
      // People & access
      { to: '/employees', label: 'Employees', slug: 'employees_page' },
      { to: '/roles', label: 'Roles & Permissions', slug: 'roles_page' },
      // System settings
      { to: '/admin/settings', label: 'Settings', slug: 'settings_page' },
      // Organisation
      { to: '/master-data/companies', label: 'Companies', slug: 'companies_page' },
      { to: '/master-data/sites', label: 'Sites', slug: 'sites_page' },
      { to: '/master-data/depots', label: 'Depots', slug: 'depots_page' },
      { to: '/master-data/branches', label: 'Branches', slug: 'branches_page' },
      { to: '/master-data/service-locations', label: 'Service Locations', slug: 'service_locations_page' },
      { to: '/master-data/departments', label: 'Departments', slug: 'departments_page' },
      // Fleet taxonomy
      { to: '/master-data/vehicle-types', label: 'Vehicle Types', slug: 'vehicle_types_page' },
      { to: '/master-data/vehicle-groups', label: 'Vehicle Groups', slug: 'vehicle_groups_page' },
      // External parties
      { to: '/master-data/customers', label: 'Customers', slug: 'customers_page' },
      { to: '/master-data/vendors', label: 'Vendors', slug: 'vendors_page' },
      // Servicing catalog. Job Codes is hidden from the nav — it
      // remains a referenced lookup (ServiceHistory.jobCode etc.) but
      // there's no admin surface for editing the catalogue.
      { to: '/servicing/plans', label: 'Service Plans', slug: 'service_plans_page' },
      { to: '/servicing/schedules', label: 'Service Schedules', slug: 'service_plan_schedules_page' },
      { to: '/servicing-extras/checklists', label: 'Check List Items', slug: 'checklists_page' },
      // Inventory
      { to: '/operations/materials', label: 'Materials', slug: 'materials_page' },
      { to: '/operations/catalogue-items', label: 'Products', slug: 'catalogue_items_page' },
      { to: '/master-data/units', label: 'Units', slug: 'units_page' },
      // Dispatch config
      { to: '/master-data/dispatch-groups', label: 'Dispatch Groups', slug: 'dispatch_groups_page' },
      // Communication audit logs — read-only history of every email + SMS
      // dispatched by the system (magic links, invitations, IoT notifications,
      // password resets, etc.). Useful for ops debugging "user didn't get email".
      { to: '/integration/email-history', label: 'Email History', slug: 'settings_page' },
      { to: '/integration/sms-history',   label: 'SMS History',   slug: 'settings_page' },
      { to: '/integration/api-call-logs', label: 'API Call Logs', slug: 'settings_page' },
      // API keys for partner integrations. Its own slug — minting credentials
      // is a higher bar than reading the logs above it.
      { to: '/admin/developer', label: 'Developer', slug: 'api_keys' },
    ],
  },
];

export function Sidebar({ collapsed = false }: { collapsed?: boolean }) {
  const { can } = usePermissions();
  const [open, setOpen] = React.useState<Record<string, boolean>>(() =>
    Object.fromEntries(SIDEBAR_GROUPS.map((g) => [g.label, g.label === 'Dashboard' || g.label === 'Assets']))
  );

  const visibleGroups = SIDEBAR_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => !i.slug || can(i.slug, 'view')) }))
    .filter((g) => g.items.length > 0);

  return (
    <>
      <aside
        className={cn(
          'w-64 shrink-0 border-r bg-sidebar text-sidebar-foreground flex-col',
          // Below lg (phones + tablets, both orientations): hidden — the bottom
          // nav + "More" sheet handle navigation. The dense sidebar+table
          // desktop layout only makes sense once there's ≥1024px of width.
          // `collapsed` (toggled from the header on lg+) hides it everywhere so
          // desktop/landscape-tablet users can reclaim the 256px.
          collapsed ? 'hidden' : 'hidden lg:flex lg:static lg:h-screen lg:sticky lg:top-0',
        )}
      >
      <div className="px-4 py-4 border-b flex items-center gap-2">
        <Truck className="h-5 w-5 text-primary" />
        <div className="font-semibold text-base">Atlas Fleet</div>
      </div>
      <nav className="flex-1 overflow-y-auto p-2 text-sm">
        {visibleGroups.map((g) => {
          const GroupIcon = g.icon;
          const isOpen = open[g.label];
          return (
            <div key={g.label} className="mb-1">
              <button
                onClick={() => setOpen((s) => ({ ...s, [g.label]: !s[g.label] }))}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-sidebar-accent text-left font-medium text-muted-foreground"
              >
                <GroupIcon className="h-4 w-4" />
                <span className="flex-1">{g.label}</span>
                {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              </button>
              {isOpen && (
                <div className="ml-2 mt-0.5 space-y-0.5">
                  {g.items.map((i) => (
                    <NavLink
                      key={i.to}
                      to={i.to}
                      className={({ isActive }) =>
                        cn(
                          'block px-3 py-1.5 rounded-md transition-colors',
                          isActive
                            ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                            : 'hover:bg-sidebar-accent/50'
                        )
                      }
                    >
                      {i.label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      </aside>
    </>
  );
}
